import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useJobFilterStore } from "../../store/useJobFilterStore";
import { useCandidateProfileStore } from "../../store/useCandidateProfileStore";
import { useBasicProfileStore } from "../../store/useBasicProfileStore";
import DatePicker from "react-datepicker";

import PhoneInput from "react-phone-number-input";
import { toast } from "react-toastify";

import "react-phone-number-input/style.css";

export default function EditBasicProfileForm({ onClose, setBasicInformation, defaultData }: any) {
  const info = defaultData?.[0] ?? {};
  const location = useLocation();
  const lang = location.pathname.startsWith("/id") ? "id" : "en";


  const { profile, fetchCandidateProfile } = useCandidateProfileStore();

  useEffect(() => {
    fetchCandidateProfile();
  }, [fetchCandidateProfile]);


  const [countryId, setCountryId] = useState(profile?.country_id?.toString() ?? "");
  const [stateId, setStateId] = useState(profile?.state_id?.toString() ?? "");
  const [cityId, setCityId] = useState(profile?.city_id?.toString() ?? "");

  const { genders, degrees, countries, states, cities, experiences, experienceLevels, martialStatus, fetchStates, fetchCities, fetchFilters } = useJobFilterStore();

  useEffect(() => {
    if (!profile) return;

    setCountryId(profile.country_id?.toString() ?? "");
    setStateId(profile.state_id?.toString() ?? "");
    setCityId(profile.city_id?.toString() ?? "");
  }, [profile]);

  useEffect(() => {
    if (!countryId) {
      setStateId("");
      setCityId("");
      return;
    }

    fetchStates(Number(countryId), lang);
  }, [countryId, fetchStates, lang]);

  // fetch cities ketika state berubah
  useEffect(() => {
    if (!stateId) {
      setCityId("");
      return;
    }
    fetchCities(Number(stateId), lang);
    setCityId("");
  }, [stateId, fetchCities, lang]);

  useEffect(() => {
    if (!stateId) {
      setCityId("");
      return;
    }

    fetchCities(Number(stateId), lang);
  }, [stateId, fetchCities, lang]);

  useEffect(() => {
    if (lang) fetchFilters(lang, "locations");
  }, [lang]);

  const toDate = (value?: string) =>
    value ? new Date(value) : null;

  const toString = (date: Date | null) =>
    date ? date.toISOString().slice(0, 10) : "";


  const [form, setForm] = useState({
    fullName: info.name ?? "",
    phoneNumber: profile?.phone ?? "",
    location: info.location ?? "",
    gender: info.gender ?? "",
    martialStatus: info.martialStatus ?? "",
    experience: info.experience ?? "",
    degree: info.degree ?? "",
    experienceLevel: info.experienceLevel ?? "",
    birthDate: info.birth
      ? info.birth.split("T")[0]
      : "",
    about: info.about ?? "",
    imgProfile: info.imgProfile ?? "",
    country: info?.country,
    state: info?.state,
    city: info?.city,
  });

  const handleChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const [avatar, setAvatar] = useState(
    form.imgProfile || "https://placehold.co/200"
  );
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  const { updateBasicProfile, loading } = useBasicProfileStore();

  const REQUIRED_FIELDS = [
    "fullName",
    "phoneNumber",
    "birthDate",
    "gender",
    "martialStatus",
    "degree",
    "experience",
    "experienceLevel",
    "about",
  ];

  const REQUIRED_LOCATION = ["countryId", "stateId", "cityId"];
  const PHOTO_REQUIRED = true;
  const isFormValid = () => {
    // cek field text / select
    for (const field of REQUIRED_FIELDS) {
      if (!form[field as keyof typeof form]) return false;
    }

    // cek location
    if (!countryId || !stateId || !cityId) return false;

    // cek photo
    if (PHOTO_REQUIRED && !avatarFile && !form.imgProfile) return false;

    return true;
  };


  const handleSave = async () => {
    if (!isFormValid()) return;

    await updateBasicProfile({
      photo: avatarFile,
      name: form.fullName,
      phone: form.phoneNumber,
      birth_date: form.birthDate,
      gender_id: Number(form.gender),
      martial_status_id: Number(form.martialStatus),
      degree_id: Number(form.degree),
      country_id: Number(countryId),
      state_id: Number(stateId),
      city_id: Number(cityId),
      about: form.about,
      experience_year_id: Number(form.experience),
      experience_level_id: Number(form.experienceLevel),
    });

    fetchCandidateProfile();
    onClose();
  };


  return (
    <div className="space-y-6">
      <div className="flex justify-center mb-6">
        <label className="relative cursor-pointer group">
          <div className="w-32 h-32 rounded-full border overflow-hidden">
            <img
              src={avatar}
              className="w-full h-full object-cover"
              alt="profile"
            />
          </div>

          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white rounded-full transition">
            Change Photo
          </div>

          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;

              const MAX_SIZE = 2 * 1024 * 1024; // 2MB

              if (file.size > MAX_SIZE) {
                toast.error("Ukuran foto maksimal 2 MB");
                e.target.value = ""; // reset input
                return;
              }

              setAvatar(URL.createObjectURL(file));
              setAvatarFile(file);
            }}
          />

        </label>
      </div>


      <div className="grid grid-cols-1 space-y-5">
        <div>
          <label className="text-sm font-medium">{lang === "id" ? "Nama Lengkap" : "Full Name"}</label>
          <input
            name="fullName"
            value={form.fullName}
            onChange={handleChange}
            className="w-full border rounded-lg p-3"
          />
        </div>

        <div>
          <label className="text-sm font-medium">{lang === "id" ? "Nomor Telepon" : "Phone Number"}</label>
          <PhoneInput
            defaultCountry="ID"
            international
            withCountryCallingCode
            value={form.phoneNumber}
            onChange={(value) =>
              setForm((prev) => ({ ...prev, phoneNumber: value || "" }))
            }
            className="w-full border rounded-lg pl-3 pr-1 py-1 flex flex-col md:flex-row gap-4"
          />

        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          <div>
            <label className="text-sm font-medium">
              {lang === "id" ? "Negara" : "Country"}
            </label>

            <select
              value={countryId}
              onChange={(e) => setCountryId(e.target.value)}
              className="w-full border rounded-lg p-3"
            >
              <option value="">
                {lang === "id" ? "Pilih Negara" : "Select Country"}
              </option>

              {countries.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">
              {lang === "id" ? "Provinsi" : "State"}
            </label>

            <select
              value={stateId}
              onChange={(e) => setStateId(e.target.value)}
              disabled={!countryId}
              className="w-full border rounded-lg p-3 disabled:bg-gray-100"
            >
              <option value="">
                {lang === "id" ? "Pilih Provinsi" : "Select State"}
              </option>

              {states.map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">
              {lang === "id" ? "Kota" : "City"}
            </label>

            <select
              value={cityId}
              onChange={(e) => setCityId(e.target.value)}
              disabled={!stateId}
              className="w-full border rounded-lg p-3 disabled:bg-gray-100"
            >
              <option value="">
                {lang === "id" ? "Pilih Kota" : "Select City"}
              </option>

              {cities.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>


          <div>
            <label className="text-sm font-medium">{lang === "id" ? "Gelar" : "Degree"}</label>

            <select
              name="degree"
              value={form.degree}
              onChange={handleChange}
              className="w-full border rounded-lg p-3"
            >
              <option value="">
                {lang === "id" ? "Pilih Gelar" : "Select Degree"}
              </option>
              {degrees.map((deg: any) => (
                <option key={deg.id} value={deg.id}>
                  {deg.name}
                </option>
              ))}
            </select>
          </div>



        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">{lang === "id" ? "Jenis Kelamin" : "Gender"}</label>

            <select
              name="gender"
              value={form.gender}
              onChange={handleChange}
              className="w-full border rounded-lg p-3"
            >
              <option value="">
                {lang === "id" ? "Pilih Jenis Kelamin" : "Select Gender"}
              </option>
              {genders.map((gen: any) => (
                <option key={gen.id} value={gen.id}>
                  {gen.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-sm font-medium">{lang === "id" ? "Tanggal Lahir" : "Birth Date"}</label>

            <DatePicker
              selected={toDate(form.birthDate)}
              onChange={(date: any) =>
                handleChange({
                  target: {
                    name: "birthDate",
                    value: toString(date),
                  },
                })
              }
              dateFormat="yyyy-MM-dd"
              placeholderText="YYYY-MM-DD"
              className="w-full border rounded-lg p-3"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
            />

          </div>

          <div>
            <label className="text-sm font-medium">{lang === "id" ? "Tahun Pengalaman" : "Experience Year"}</label>
            <select
              name="experience"
              value={form.experience}
              onChange={handleChange}
              className="w-full border rounded-lg p-3"
            >
              <option value="">
                {lang === "id" ? "Pilih Tahun Pengalaman" : "Select Experience Year"}
              </option>
              {experiences.map((gen: any) => (
                <option key={gen.id} value={gen.id}>
                  {gen.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">{lang === "id" ? "Tingkat Pengalaman" : "Experience Levels"}</label>

            <select
              name="experienceLevel"
              value={form.experienceLevel}
              onChange={handleChange}
              className="w-full border rounded-lg p-3"
            >
              <option value="">
                {lang === "id" ? "Pilih Tingkat Pengalaman" : "Select Experience Level"}
              </option>
              {experienceLevels.map((gen: any) => (
                <option key={gen.id} value={gen.id}>
                  {gen.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium">{lang === "id" ? "Status" : "Martial Status"}</label>

            <select
              name="martialStatus"
              value={form.martialStatus}
              onChange={handleChange}
              className="w-full border rounded-lg p-3"
            >
              <option value="">
                {lang === "id" ? "Pilih Status" : "Select Martial Status"}
              </option>
              {martialStatus.map((gen: any) => (
                <option key={gen.id} value={gen.id}>
                  {gen.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">{lang === "id" ? "Tentang Saya" : "About Me"}</label>
          <textarea
            name="about"
            value={form.about}
            onChange={handleChange}
            className="w-full border rounded-lg p-3 h-28"
          />
        </div>
      </div>

      <div className="flex justify-center gap-4 pt-2">
        <button
          onClick={handleSave}
          disabled={!isFormValid() || loading}
          className={`px-6 py-3 rounded-lg font-semibold transition
    ${!isFormValid() || loading
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-primary text-white hover:bg-primary/90"
            }`}
        >
          {lang === "id" ? "Simpan" : "Save"}
        </button>

      </div>
    </div>
  );
}
